import {
  BarChart,
  Business,
  EmojiEvents,
  Home,
  Inventory,
  People,
  PointOfSale,
  ShoppingCart,
  ExpandMore as ExpandMoreIcon,
  CheckCircle as CheckCircleIcon,
  ReceiptLong,
  LocalOffer,
  CurrencyRupee,
  Badge,
  Category,
  Inventory2,
  Place,
  Diamond,
  Tag,
  Sell,
  Storefront,
  Layers,
  QrCode2,
  AssignmentReturn,
  SwapHoriz,
  Today,
  AppRegistration,
  PersonSearch,
  AccountBalanceWallet,
  AccountBalance,
  Receipt,
  Payments,
  LocalMall,
  Book,
  MonetizationOn,
  Percent,
  Cake,
  Favorite,
  PendingActions,
  MailOutline,
} from "@mui/icons-material";
import CancelRoundedIcon from "@mui/icons-material/CancelRounded";
import {
  Box,
  Button,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
} from "@mui/material";
import React, { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

/* ───────────── TOKENS ───────────── */
const NAVY_DEEP = "#0a1230";
const GOLD = "#e3c17e";
const GOLD_SOFT = "rgba(227,193,126,0.22)";
const HEADING_YELLOW = "#8a6d1a";

const SHEET_BG = "#fff";
const SHEET_BG_ALT = "#ffffff";
const BORDER_LIGHT = "rgba(20, 40, 37, 0.36)";

const BLUE = "#00443b";
const BLUE_SOFT = "rgba(3, 68, 57, 0.1)";
const BLUE_BORDER = "rgba(0, 70, 61, 0.3)";
const TEXT_MUTED = "rgba(28,35,51,0.55)";
const CIRCLE_GREY = "#8a94a3";

const GREEN = "#099581";
const GREEN_SOFT = "rgba(7, 139, 117, 0.14)";
const GREEN_BORDER = "rgba(12, 131, 111, 0.42)";
const GREEN_HOVER = "#0b7f6d";

const ORANGE = "#ff7a1a";
const ORANGE_SOFT = "rgba(255, 122, 26, 0.16)";
const ORANGE_BORDER = "rgba(255, 122, 26, 0.48)";
const ORANGE_HOVER = "#e8690f";

const HEADER_HEIGHT = 200;
const SHEET_OVERLAP = 26;

// small icon helper for submenu items
const ic = (Icon) => <Icon sx={{ fontSize: 16 }} />;

const ShimmerSweep = ({ delay = 0 }) => (
  <Box
    aria-hidden
    sx={{
      position: "absolute",
      top: "-60%",
      left: 0,
      width: "35%",
      height: "220%",
      background:
        "linear-gradient(90deg, transparent, rgba(255,255,255,0.65), transparent)",
      animation: "shimmerSweep 3.4s ease-in-out infinite",
      animationDelay: `${delay}s`,
      pointerEvents: "none",
    }}
  />
);

const SidebarDrawer = ({
  open,
  toggleDrawer,
  singleImage,
  userArea,
  userName,
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const adminType = localStorage.getItem("adminType");

  const logOut = () => {
    navigate("/");
    localStorage.clear();
    window.location.reload();
  };

  /* ───────────── MENUS ───────────── */

  // Dashboard — pinned at the top, outside the scrolling list
  const dashboardMenu = {
    text: "Dashboard",
    icon: <Home />,
    path: "/home",
    accent: ORANGE,
    accentBorder: ORANGE_BORDER,
    accentHover: ORANGE_HOVER,
  };

  const topTenMenu = {
    text: "Top Ten",
    icon: <EmojiEvents />,
    children: [
      {
        heading: null,
        items: [
          {
            text: "Top 10 Invoices",
            path: "/top-ten-invoices",
            icon: ic(ReceiptLong),
          },
          {
            text: "Top 10 Brands",
            path: "/top-ten-brands",
            icon: ic(LocalOffer),
          },
          {
            text: "Top 10 Prices",
            path: "/top-ten-prices",
            icon: ic(CurrencyRupee),
          },
          {
            text: "Top 10 Salesman",
            path: "/top-ten-salesman",
            icon: ic(Badge),
          },
          {
            text: "Top 10 Products",
            path: "/top-ten-products",
            icon: ic(Category),
          },
        ],
      },
    ],
  };

  const inventoryMenu = {
    text: "Inventory",
    icon: <Inventory />,
    children: [
      {
        heading: null,
        items: [
          {
            text: "Stock Point",
            path: "/stock-point",
            icon: ic(Place),
          },
          {
            text: "Commodity Wise Stock",
            path: "/commodity-wise",
            icon: ic(Diamond),
          },
          {
            text: "Product Wise Stock",
            path: "/product-wise",
            icon: ic(Inventory2),
          },
          {
            text: "HSN Wise Stock",
            path: "/hsn-wise",
            icon: ic(Tag),
          },
          {
            text: "Brand Wise Stock",
            path: "/brand-wise",
            icon: ic(Sell),
          },
          {
            text: "Dealer Wise Stock",
            path: "/dealer-wise",
            icon: ic(Storefront),
          },
          {
            text: "Group Wise Stock",
            path: "/group-wise",
            icon: ic(Layers),
          },
          { text: "Barcode Check", path: "/bar-code-check", icon: ic(QrCode2) },
        ],
      },
    ],
  };

  const pointOfSaleMenu = {
    text: "Point of Sale",
    icon: <PointOfSale />,
    children: [
      {
        heading: null,
        items: [
          {
            text: "Sale Invoice",
            path: "/bar-code-scanner",
            icon: ic(ReceiptLong),
          },
          {
            text: "Sale Return",
            path: "/sale-return",
            icon: ic(AssignmentReturn),
          },
          {
            text: "Exchange Bill",
            path: "/exchange-bill",
            icon: ic(SwapHoriz),
          },
        ],
      },
      {
        heading: "Reports",
        items: [
          {
            text: "Day Glance",
            path: "/point-of-sales/day-glance",
            icon: ic(Today),
          },
          {
            text: "Sale Register",
            path: "/point-of-sales/sale-register",
            icon: ic(AppRegistration),
          },
        ],
      },
    ],
  };

  const accountsMenu = {
    text: "Accounts",
    icon: <People />,
    children: [
      {
        heading: null,
        items: [
          {
            text: "Outstanding Customers",
            path: "/outstanding-customers",
            icon: ic(PersonSearch),
          },
          {
            text: "Outstanding Dealers",
            path: "/outstanding-dealers",
            icon: ic(Storefront),
          },
          {
            text: "Cash Book",
            path: "/cash-book",
            icon: ic(AccountBalanceWallet),
          },
          {
            text: "Bank Statement",
            path: "/bank-statement",
            icon: ic(AccountBalance),
          },
          {
            text: "Receipt Register",
            path: "/receipt-register",
            icon: ic(Receipt),
          },
          {
            text: "Payment Receipt Register",
            path: "/payment-receipt-register",
            icon: ic(Payments),
          },
          {
            text: "Purchase Register",
            path: "/purchase-register",
            icon: ic(LocalMall),
          },
          {
            text: "Journal Register",
            path: "/journal-register",
            icon: ic(Book),
          },
          {
            text: "SalesMen Wise Commission",
            path: "/salesmen-commission",
            icon: ic(MonetizationOn),
          },
        ],
      },
    ],
  };

  const purchaseMenu = {
    text: "Purchase",
    icon: <ShoppingCart />,
    children: [
      {
        heading: null,
        items: [
          {
            text: "Commodity Wise Purchase",
            path: "/purchase/commodity-wise",
            icon: ic(Diamond),
          },
          {
            text: "Product Wise Purchase",
            path: "/purchase/product-wise",
            icon: ic(Inventory2),
          },
          {
            text: "Dealer Wise Purchase",
            path: "/purchase/dealer-wise",
            icon: ic(Storefront),
          },
          {
            text: "HSN Wise Purchase",
            path: "/purchase/hsn-wise",
            icon: ic(Tag),
          },
          {
            text: "Brand Wise Purchase",
            path: "/purchase/brand-wise",
            icon: ic(Sell),
          },
          {
            text: "Tax Wise Purchase",
            path: "/purchase/tax-wise",
            icon: ic(Percent),
          },
        ],
      },
    ],
  };

  const crmMenu = {
    text: "CRM",
    icon: <Business />,
    children: [
      {
        heading: null,
        items: [
          { text: "Today BirthDays", path: "/crm/birthdays", icon: ic(Cake) },
          {
            text: "Today Anniversaries",
            path: "/crm/anniversaries",
            icon: ic(Favorite),
          },
          { text: "Today Dues", path: "/crm/dues", icon: ic(PendingActions) },
          { text: "Graphs", path: "/graphs", icon: ic(BarChart) },
          // {
          //   text: "Mail Book Register",
          //   path: "/crm/mail-book",
          //   icon: ic(MailOutline),
          // },
        ],
      },
    ],
  };

  // Admin sees everything; non-admin keeps the reduced menu from the old code
  const menuItems =
    "ADMIN" === "ADMIN"
      ? [
          topTenMenu,
          inventoryMenu,
          pointOfSaleMenu,
          accountsMenu,
          purchaseMenu,
          crmMenu,
        ]
      : [pointOfSaleMenu, crmMenu];

  const flattenChildItems = (item) =>
    Array.isArray(item.children) ? item.children.flatMap((g) => g.items) : [];

  const parentOfActiveChild = menuItems.find((item) =>
    flattenChildItems(item).some((c) => location.pathname === c.path),
  )?.text;

  const [expandedMenu, setExpandedMenu] = useState(parentOfActiveChild ?? null);

  const hasLogo = Boolean(singleImage);

  /* ───────────── RENDER ───────────── */
  return (
    <Drawer
      anchor="top"
      open={open}
      onClose={toggleDrawer}
      sx={{
        "& .MuiDrawer-paper": {
          width: "100%",
          maxWidth: "100%",
          height: "100dvh",
          maxHeight: "100dvh",
          backgroundColor: NAVY_DEEP,
          color: "white",
          borderRadius: 0,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        },
      }}
    >
      {/* ── HEADER BAND ── */}
      <Box
        sx={{
          position: "relative",
          width: "100%",
          height: `${HEADER_HEIGHT}px`,
          flexShrink: 0,
          overflow: "hidden",
          backgroundColor: NAVY_DEEP,
          "@keyframes headerShimmer": {
            "0%": { transform: "translateX(-120%) rotate(8deg)" },
            "100%": { transform: "translateX(220%) rotate(8deg)" },
          },
        }}
      >
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            background: `radial-gradient(circle at 15% 15%, rgba(227,193,126,0.10), transparent 55%), linear-gradient(180deg, ${NAVY_DEEP} 0%, #060a1e 100%)`,
          }}
        />
        <Box
          aria-hidden
          sx={{
            position: "absolute",
            inset: 0,
            overflow: "hidden",
            pointerEvents: "none",
          }}
        >
          <Box
            sx={{
              position: "absolute",
              top: "-30%",
              left: 0,
              width: "28%",
              height: "160%",
              background:
                "linear-gradient(100deg, transparent, rgba(227,193,126,0.22), transparent)",
              animation: "headerShimmer 5s ease-in-out infinite",
            }}
          />
        </Box>

        <IconButton
          onClick={toggleDrawer}
          sx={{
            position: "absolute",
            top: 8,
            right: 8,
            color: "rgba(255,255,255,0.9)",
            zIndex: 2,
          }}
          size="small"
        >
          <CancelRoundedIcon style={{ fontSize: 38 }} />
        </IconButton>

        <Box
          sx={{
            position: "absolute",
            left: 18,
            right: 50,
            bottom: 45,
            zIndex: 1,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: "12px",
          }}
        >
          <Box
            sx={{
              width: 65,
              height: 65,
              borderRadius: "50%",
              overflow: "hidden",
              backgroundColor: "#ffffff",
              border: `2px solid ${GOLD}`,
              boxShadow: "0 2px 12px rgba(0,0,0,0.55)",
              backgroundImage: hasLogo ? `url('${singleImage}')` : "none",
              backgroundSize: "cover",
              backgroundPosition: "center",
            }}
          />
          <Typography
            sx={{
              fontWeight: 800,
              letterSpacing: "0.04em",
              color: GOLD,
              fontSize: "18px",
              lineHeight: 1.2,
              textShadow: "0 2px 10px rgba(0,0,0,0.75)",
              maxWidth: "100%",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {userName || "\u00A0"}
          </Typography>
          {userArea && (
            <Box
              sx={{
                px: 0.9,
                py: 0.15,
                borderRadius: "6px",
                border: `1px solid ${GOLD_SOFT}`,
                backgroundColor: "rgba(227,193,126,0.12)",
              }}
            >
              <Typography
                sx={{
                  fontSize: "9.5px",
                  fontWeight: 700,
                  letterSpacing: "0.06em",
                  textTransform: "uppercase",
                  color: GOLD,
                }}
              >
                {userArea}
              </Typography>
            </Box>
          )}
        </Box>
      </Box>

      {/* ── MENU SHEET ── */}
      <Box
        sx={{
          position: "relative",
          zIndex: 3,
          mt: `-${SHEET_OVERLAP}px`,
          flex: 1,
          display: "flex",
          flexDirection: "column",
          minHeight: 0,
          borderRadius: "26px 26px 0 0",
          backgroundColor: SHEET_BG,
          boxShadow: "0 -10px 24px rgba(20,25,40,0.14)",
          "@keyframes sheetShimmer": {
            "0%": { transform: "translateX(-140%) rotate(10deg)" },
            "100%": { transform: "translateX(260%) rotate(10deg)" },
          },
          "@keyframes shimmerSweep": {
            "0%": { transform: "translateX(-120%) rotate(20deg)" },
            "100%": { transform: "translateX(220%) rotate(20deg)" },
          },
          "@keyframes barShimmer": {
            "0%": { transform: "translateX(-140%)" },
            "100%": { transform: "translateX(240%)" },
          },
          "@keyframes breathe": {
            "0%, 100%": { boxShadow: `0 0 0 0 ${GREEN_SOFT}` },
            "50%": { boxShadow: `0 0 0 5px rgba(22,101,52,0)` },
          },
          "@keyframes breatheOnGreen": {
            "0%, 100%": { boxShadow: "0 0 0 0 rgba(255,255,255,0.45)" },
            "50%": { boxShadow: "0 0 0 5px rgba(255,255,255,0)" },
          },
          "@keyframes drift1": {
            "0%, 100%": { transform: "translate(0, 0) scale(1)" },
            "50%": { transform: "translate(10px, -18px) scale(1.05)" },
          },
          "@keyframes drift2": {
            "0%, 100%": { transform: "translate(0, 0) scale(1)" },
            "50%": { transform: "translate(-14px, 14px) scale(0.96)" },
          },
          "@media (prefers-reduced-motion: reduce)": {
            "& *": { animation: "none !important" },
          },
        }}
      >
        {/* floating decorative circles */}
        <Box
          aria-hidden
          sx={{
            position: "absolute",
            inset: 0,
            overflow: "hidden",
            borderRadius: "26px 26px 0 0",
            zIndex: 0,
            pointerEvents: "none",
          }}
        >
          <Box
            sx={{
              position: "absolute",
              top: "-40px",
              left: "-50px",
              width: 220,
              height: 220,
              borderRadius: "50%",
              backgroundColor: CIRCLE_GREY,
              opacity: 0.1,
              animation: "drift1 16s ease-in-out infinite",
            }}
          />
          <Box
            sx={{
              position: "absolute",
              top: "10%",
              right: "-70px",
              width: 190,
              height: 190,
              borderRadius: "50%",
              border: `2px solid ${CIRCLE_GREY}`,
              opacity: 0.16,
              animation: "drift2 20s ease-in-out infinite",
            }}
          />
          <Box
            sx={{
              position: "absolute",
              bottom: "-60px",
              left: "18%",
              width: 200,
              height: 200,
              borderRadius: "50%",
              backgroundColor: CIRCLE_GREY,
              opacity: 0.09,
              animation: "drift2 24s ease-in-out infinite",
            }}
          />
          <Box
            sx={{
              position: "absolute",
              top: "-10%",
              left: 0,
              width: "22%",
              height: "140%",
              background:
                "linear-gradient(100deg, transparent, rgba(9,149,129,0.06), transparent)",
              animation: "sheetShimmer 7s ease-in-out infinite",
            }}
          />
        </Box>

        {/* pull tab */}
        <Box
          sx={{
            position: "relative",
            zIndex: 1,
            display: "flex",
            justifyContent: "center",
            pt: "9px",
            pb: "3px",
          }}
        >
          <Box
            sx={{
              width: 15,
              height: 15,
              backgroundColor: GOLD,
              opacity: 0.9,
              transform: "rotate(45deg)",
              borderRadius: "3px",
              boxShadow: "0 0 10px rgba(227,193,126,0.35)",
            }}
          />
        </Box>

        {/* ── FIXED DASHBOARD (always on top, never scrolls) ── */}
        <Box
          sx={{
            position: "relative",
            zIndex: 1,
            px: 1.25,
            pt: 0.5,
            pb: 0.75,
            flexShrink: 0,
          }}
        >
          {(() => {
            const isActive = location.pathname === dashboardMenu.path;
            return (
              <Box
                sx={{
                  borderRadius: "14px",
                  overflow: "hidden",
                  border: `1px solid ${dashboardMenu.accentBorder}`,
                }}
              >
                <ListItemButton
                  onClick={() => {
                    navigate(dashboardMenu.path);
                    toggleDrawer();
                  }}
                  sx={{
                    py: 1.05,
                    px: 1.25,
                    minHeight: 48,
                    position: "relative",
                    overflow: "hidden",
                    backgroundColor: ORANGE,
                    "&:hover": { backgroundColor: ORANGE_HOVER },
                    "&:focus-visible": {
                      outline: `2px solid ${GOLD}`,
                      outlineOffset: "-2px",
                    },
                  }}
                >
                  <Box
                    aria-hidden
                    sx={{
                      position: "absolute",
                      inset: 0,
                      overflow: "hidden",
                      pointerEvents: "none",
                    }}
                  >
                    <Box
                      sx={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        width: "40%",
                        height: "100%",
                        background:
                          "linear-gradient(100deg, transparent, rgba(255,255,255,0.35), transparent)",
                        animation: "barShimmer 3.2s ease-in-out infinite",
                      }}
                    />
                  </Box>
                  <ListItemIcon
                    sx={{ minWidth: 36, position: "relative", zIndex: 1 }}
                  >
                    <Box
                      sx={{
                        position: "relative",
                        width: 25,
                        height: 25,
                        borderRadius: "8px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        overflow: "hidden",
                        background: "#fff",
                        color: ORANGE,
                        fontSize: 14,
                        animation: "breatheOnGreen 2.4s ease-in-out infinite",
                        "& svg": { fontSize: 16 },
                      }}
                    >
                      {dashboardMenu.icon}
                      <ShimmerSweep />
                    </Box>
                  </ListItemIcon>
                  <ListItemText
                    primary={dashboardMenu.text}
                    primaryTypographyProps={{
                      fontWeight: 700,
                      color: "#fff",
                      fontSize: "14px",
                    }}
                    sx={{ position: "relative", zIndex: 1 }}
                  />
                  {isActive && (
                    <Box
                      sx={{
                        position: "relative",
                        zIndex: 1,
                        width: 6,
                        height: 6,
                        borderRadius: "50%",
                        backgroundColor: GOLD,
                        ml: 1,
                      }}
                    />
                  )}
                </ListItemButton>
              </Box>
            );
          })()}
        </Box>

        {/* ── SCROLLING MENU LIST ── */}
        <List
          sx={{
            px: 1.25,
            pt: 0.5,
            pb: 1.25,
            flex: 1,
            overflowY: "auto",
            position: "relative",
            zIndex: 1,
          }}
        >
          {menuItems.map((item, index) => {
            const isExpanded = expandedMenu === item.text;
            const isChildActive = flattenChildItems(item).some(
              (c) => location.pathname === c.path,
            );
            const active = isChildActive || isExpanded;

            return (
              <Box
                key={item.text}
                sx={{
                  mb: 0.75,
                  borderRadius: "14px",
                  overflow: "hidden",
                  backgroundColor: isExpanded ? SHEET_BG_ALT : "transparent",
                  border: `1px solid ${active ? GREEN_BORDER : isExpanded ? BORDER_LIGHT : GREEN_BORDER}`,
                  transition:
                    "background-color 0.2s ease, border-color 0.2s ease",
                }}
              >
                <ListItemButton
                  onClick={() =>
                    setExpandedMenu((prev) =>
                      prev === item.text ? null : item.text,
                    )
                  }
                  sx={{
                    width: "100%",
                    borderRadius: isExpanded ? 0 : "14px",
                    py: 1.05,
                    px: 1.25,
                    minHeight: 48,
                    position: "relative",
                    overflow: "hidden",
                    backgroundColor: active ? GREEN : "transparent",
                    transition: "background-color 0.15s ease",
                    "&:hover": {
                      backgroundColor: active
                        ? GREEN_HOVER
                        : "rgba(20, 40, 37, 0.42)",
                    },
                    "&:focus-visible": {
                      outline: `2px solid ${GOLD}`,
                      outlineOffset: "-2px",
                    },
                  }}
                >
                  <Box
                    aria-hidden
                    sx={{
                      position: "absolute",
                      inset: 0,
                      overflow: "hidden",
                      pointerEvents: "none",
                    }}
                  >
                    <Box
                      sx={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        width: "40%",
                        height: "100%",
                        background:
                          "linear-gradient(100deg, transparent, rgba(255,255,255,0.35), transparent)",
                        animation: "barShimmer 3.2s ease-in-out infinite",
                        animationDelay: `${index * 0.15}s`,
                      }}
                    />
                  </Box>
                  <ListItemIcon
                    sx={{ minWidth: 36, position: "relative", zIndex: 1 }}
                  >
                    <Box
                      sx={{
                        position: "relative",
                        width: 25,
                        height: 25,
                        borderRadius: "8px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        overflow: "hidden",
                        background: active ? "#ffffff" : BLUE_SOFT,
                        border: active ? "none" : `1px solid ${BLUE_BORDER}`,
                        color: active ? GREEN : BLUE,
                        animation: active
                          ? "breatheOnGreen 2.4s ease-in-out infinite"
                          : "none",
                        "& svg": { fontSize: 16 },
                      }}
                    >
                      {item.icon}
                      <ShimmerSweep delay={index * 0.2} />
                    </Box>
                  </ListItemIcon>
                  <ListItemText
                    primary={item.text}
                    primaryTypographyProps={{
                      fontWeight: active ? 700 : 600,
                      color: active ? "#ffffff" : BLUE,
                      fontSize: "14px",
                    }}
                    sx={{ position: "relative", zIndex: 1 }}
                  />
                  <ExpandMoreIcon
                    sx={{
                      position: "relative",
                      zIndex: 1,
                      color: active ? "#ffffff" : TEXT_MUTED,
                      fontSize: 19,
                      transform: isExpanded ? "rotate(180deg)" : "rotate(0deg)",
                      transition: "transform 0.25s ease",
                    }}
                  />
                </ListItemButton>

                {/* submenu groups */}
                <Box
                  sx={{
                    maxHeight: isExpanded ? 3000 : 0,
                    opacity: isExpanded ? 1 : 0,
                    overflow: "hidden",
                    transition: "max-height 0.4s ease, opacity 0.22s ease",
                  }}
                >
                  {item.children.map((group, groupIndex) => (
                    <Box key={groupIndex}>
                      <Box
                        sx={{
                          mx: 1.25,
                          borderTop: `1px solid ${BORDER_LIGHT}`,
                        }}
                      />
                      {group.heading && (
                        <Typography
                          sx={{
                            px: 1.75,
                            pt: 1,
                            pb: 0.25,
                            fontSize: "10.5px",
                            fontWeight: 800,
                            letterSpacing: "0.09em",
                            textTransform: "uppercase",
                            color: HEADING_YELLOW,
                          }}
                        >
                          {group.heading}
                        </Typography>
                      )}
                      <Box
                        sx={{
                          p: 1,
                          display: "grid",
                          gridTemplateColumns:
                            "repeat(auto-fill, minmax(84px, 1fr))",
                          gap: "8px",
                        }}
                      >
                        {group.items.map((subItem, subIndex) => {
                          const isActive = location.pathname === subItem.path;
                          const go = () => {
                            navigate(subItem.path);
                            toggleDrawer();
                          };
                          return (
                            <Box
                              key={subItem.path + subIndex}
                              role="button"
                              tabIndex={0}
                              onClick={go}
                              onKeyDown={(e) => {
                                if (e.key === "Enter" || e.key === " ") go();
                              }}
                              sx={{
                                position: "relative",
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                                justifyContent: "center",
                                textAlign: "center",
                                gap: "5px",
                                py: 1,
                                px: 0.5,
                                borderRadius: "10px",
                                cursor: "pointer",
                                userSelect: "none",
                                backgroundColor: isActive
                                  ? GREEN_SOFT
                                  : "rgba(20,25,40,0.035)",
                                border: isActive
                                  ? `1px solid ${GREEN}`
                                  : "1px solid #00685759",
                                transition:
                                  "transform 0.16s ease, background-color 0.16s ease, border-color 0.16s ease",
                                "&:hover": {
                                  transform: "translateY(-2px)",
                                  backgroundColor: isActive
                                    ? GREEN_SOFT
                                    : "rgba(20,25,40,0.06)",
                                  borderColor: isActive ? GREEN : GOLD_SOFT,
                                },
                                "&:active": {
                                  transform: "translateY(0) scale(0.97)",
                                },
                                "&:focus-visible": {
                                  outline: `2px solid ${GOLD}`,
                                  outlineOffset: "2px",
                                },
                              }}
                            >
                              <Box
                                sx={{
                                  position: "relative",
                                  width: 32,
                                  height: 32,
                                  borderRadius: "50%",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  overflow: "hidden",
                                  backgroundColor: isActive
                                    ? GREEN
                                    : "#0068573b",
                                  color: isActive ? "#ffffff" : GREEN,
                                  animation: isActive
                                    ? "breathe 2.4s ease-in-out infinite"
                                    : "none",
                                }}
                              >
                                {subItem.icon}
                                <ShimmerSweep delay={subIndex * 0.25} />
                              </Box>
                              <Typography
                                sx={{
                                  fontSize: "10px",
                                  lineHeight: 1.15,
                                  fontWeight: isActive ? 900 : 700,
                                  color: isActive ? GREEN : "#33405a",
                                }}
                              >
                                {subItem.text}
                              </Typography>
                              {isActive && (
                                <CheckCircleIcon
                                  sx={{
                                    position: "absolute",
                                    top: 3,
                                    right: 3,
                                    fontSize: 12,
                                    color: GOLD,
                                  }}
                                />
                              )}
                            </Box>
                          );
                        })}
                      </Box>
                    </Box>
                  ))}
                </Box>
              </Box>
            );
          })}
        </List>

        {/* ── FOOTER ── */}
        <Box
          sx={{
            flexShrink: 0,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            px: 2.25,
            py: 1.5,
            borderTop: `1px solid ${BORDER_LIGHT}`,
            position: "relative",
            zIndex: 1,
          }}
        >
          <Button
            variant="contained"
            sx={{
              backgroundColor: BLUE,
              color: "#fff",
              border: `1px solid ${BLUE_BORDER}`,
              textTransform: "none",
              fontWeight: 800,
              borderRadius: "10px",
              px: 4,
              py: 0.5,
              fontSize: "15px",
              boxShadow: "none",
              "&:hover": {
                backgroundColor: GREEN_SOFT,
                color: GREEN,
                borderColor: GREEN_BORDER,
                boxShadow: "none",
              },
            }}
            onClick={logOut}
          >
            Logout
          </Button>
          <Typography
            variant="body2"
            sx={{ color: TEXT_MUTED, fontSize: "12px" }}
          >
            V 1.0
          </Typography>
        </Box>
      </Box>
    </Drawer>
  );
};

export default SidebarDrawer;
